import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import ZaloLink from "../../components/feature/ZaloLink";
import { supabase } from "../../lib/supabase";
import ChatBubble, {
  ChatMsg,
  PdfViewerModal,
  MessageContextMenu,
  useScrollToBottom,
} from "../../components/feature/ChatMessage";

const ZALO_LINK = "https://zalo.me/0965180170";

interface InquiryWithMessages {
  id: number;
  product_name: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  status: string;
  reply_message?: string;
  replied_at?: string;
  created_at: string;
  messages?: ChatMsg[];
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  new: { label: "Đang chờ xử lý", color: "bg-blue-100 text-blue-700", icon: "ri-time-line" },
  read: { label: "Đã tiếp nhận", color: "bg-amber-100 text-amber-700", icon: "ri-eye-line" },
  replied: { label: "Đã phản hồi", color: "bg-green-100 text-green-700", icon: "ri-checkbox-circle-line" },
  closed: { label: "Hoàn thành", color: "bg-gray-100 text-gray-600", icon: "ri-check-double-line" },
};

const MyRequestsPage = () => {
  const [step, setStep] = useState<"search" | "results" | "detail">("search");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [inquiries, setInquiries] = useState<InquiryWithMessages[]>([]);
  const [selected, setSelected] = useState<InquiryWithMessages | null>(null);
  const [error, setError] = useState("");
  const [customerReply, setCustomerReply] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [attachFile, setAttachFile] = useState<File | null>(null);
  const [attachUploading, setAttachUploading] = useState(false);
  const [pdfViewer, setPdfViewer] = useState<{ url: string; fileName?: string } | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; msg: ChatMsg } | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [editingMsg, setEditingMsg] = useState<ChatMsg | null>(null);
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [chatSearch, setChatSearch] = useState("");
  const [chatSearchOpen, setChatSearchOpen] = useState(false);
  const [chatSearchResults, setChatSearchResults] = useState<number[]>([]);
  const [chatSearchIdx, setChatSearchIdx] = useState(0);

  // Chat container ref — scroll inside this, NOT the whole page
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLTextAreaElement>(null);
  const msgRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Auto-scroll inside chat container only
  const scrollToBottom = useScrollToBottom(chatContainerRef, [selected?.messages?.length]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const emailTrim = email.trim().toLowerCase();
    const phoneTrim = phone.trim();
    if (!emailTrim && !phoneTrim) {
      setError("Vui lòng nhập email hoặc số điện thoại để tra cứu.");
      return;
    }
    setSearchLoading(true);

    let query = supabase.from("inquiries").select("*").order("created_at", { ascending: false });
    if (emailTrim && phoneTrim) {
      query = query.or(`email.ilike.%${emailTrim}%,phone.eq.${phoneTrim}`);
    } else if (emailTrim) {
      query = query.ilike("email", `%${emailTrim}%`);
    } else {
      query = query.eq("phone", phoneTrim);
    }

    const { data, error: err } = await query;
    setSearchLoading(false);
    if (err) { setError("Lỗi tra cứu: " + err.message); return; }
    if (!data || data.length === 0) {
      setError("Không tìm thấy yêu cầu nào với thông tin này. Vui lòng kiểm tra lại email/SĐT.");
      return;
    }
    setInquiries(data as InquiryWithMessages[]);
    setStep("results");
  };

  const openDetail = async (inq: InquiryWithMessages) => {
    const { data: msgs } = await supabase
      .from("consultation_messages")
      .select("*")
      .eq("inquiry_id", inq.id)
      .order("created_at", { ascending: true });
    const loaded = { ...inq, messages: (msgs as ChatMsg[]) ?? [] };
    setSelected(loaded);
    setStep("detail");
    setChatSearch("");
    setChatSearchOpen(false);
    // Scroll inside container after render
    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }, 250);
  };

  // ── Chat search logic ──
  useEffect(() => {
    if (!chatSearch.trim() || !selected?.messages) {
      setChatSearchResults([]);
      setChatSearchIdx(0);
      return;
    }
    const q = chatSearch.toLowerCase();
    const idxs: number[] = [];
    (selected.messages ?? []).forEach((msg, i) => {
      if (msg.message?.toLowerCase().includes(q)) idxs.push(i);
    });
    setChatSearchResults(idxs);
    setChatSearchIdx(0);
    if (idxs.length > 0) {
      const msgId = (selected.messages ?? [])[idxs[0]]?.id;
      if (msgId && msgRefs.current[msgId]) {
        msgRefs.current[msgId]?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [chatSearch, selected?.messages]);

  const navigateSearch = (dir: "prev" | "next") => {
    if (!chatSearchResults.length || !selected?.messages) return;
    const newIdx = dir === "next"
      ? (chatSearchIdx + 1) % chatSearchResults.length
      : (chatSearchIdx - 1 + chatSearchResults.length) % chatSearchResults.length;
    setChatSearchIdx(newIdx);
    const msgId = (selected.messages ?? [])[chatSearchResults[newIdx]]?.id;
    if (msgId && msgRefs.current[msgId]) {
      msgRefs.current[msgId]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // ── Realtime: chỉ nhận từ DB, KHÔNG optimistic update ──
  useEffect(() => {
    if (!selected?.id) return;
    const channel = supabase
      .channel(`customer-chat-v3-${selected.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "consultation_messages",
          filter: `inquiry_id=eq.${selected.id}`,
        },
        (payload) => {
          const newMsg = payload.new as ChatMsg;
          setSelected((prev) => {
            if (!prev) return prev;
            // Deduplicate by id
            const exists = (prev.messages ?? []).some((m) => m.id === newMsg.id);
            if (exists) return prev;
            return { ...prev, messages: [...(prev.messages ?? []), newMsg] };
          });
          // Scroll inside container only
          setTimeout(() => {
            if (chatContainerRef.current) {
              chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
            }
          }, 100);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "consultation_messages",
          filter: `inquiry_id=eq.${selected.id}`,
        },
        (payload) => {
          const updated = payload.new as ChatMsg;
          setSelected((prev) => {
            if (!prev) return prev;
            return { ...prev, messages: (prev.messages ?? []).map((m) => m.id === updated.id ? updated : m) };
          });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "consultation_messages",
          filter: `inquiry_id=eq.${selected.id}`,
        },
        (payload) => {
          const deleted = payload.old as { id?: number };
          if (deleted.id) {
            setSelected((prev) =>
              prev ? { ...prev, messages: (prev.messages ?? []).filter((m) => m.id !== deleted.id) } : prev
            );
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [selected?.id]);

  const uploadAttachment = async (file: File): Promise<{ url: string; isPdf: boolean; mimeType: string } | null> => {
    setAttachUploading(true);
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    const baseName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `chat-attachments/${Date.now()}_${baseName}`;
    const isPdfFile = ext === "pdf" || file.type === "application/pdf";
    const contentType = isPdfFile
      ? "application/pdf"
      : file.type || "application/octet-stream";

    const { data, error } = await supabase.storage
      .from("bizwell-images")
      .upload(path, file, { upsert: true, contentType, cacheControl: "3600" });

    setAttachUploading(false);
    if (error || !data?.path) {
      console.error("Upload error:", error);
      return null;
    }

    const { data: urlData } = supabase.storage
      .from("bizwell-images")
      .getPublicUrl(data.path);

    if (!urlData?.publicUrl) return null;
    return { url: urlData.publicUrl, isPdf: isPdfFile, mimeType: contentType };
  };

  // ── Send message — NO optimistic update, wait for realtime to add it ──
  const sendCustomerMessage = async () => {
    if (!selected?.id || (!customerReply.trim() && !attachFile)) return;
    setSendingReply(true);
    setSendError(null);

    let attachmentUrl: string | undefined;
    let attachFileName: string | undefined;
    let attachMimeType: string | undefined;
    let isPdfAttach = false;

    if (attachFile) {
      const result = await uploadAttachment(attachFile);
      if (!result) {
        setSendError("Upload file thất bại. Vui lòng thử lại.");
        setSendingReply(false);
        return;
      }
      attachmentUrl = result.url;
      isPdfAttach = result.isPdf;
      attachFileName = attachFile.name;
      attachMimeType = result.mimeType;
    }

    const msgText = customerReply.trim()
      || (isPdfAttach ? "Đã gửi một tài liệu PDF" : attachFile ? "Đã gửi một tệp đính kèm" : "Tin nhắn");

    const insertPayload: Record<string, unknown> = {
      inquiry_id: selected.id,
      sender_type: "customer",
      sender_name: selected.name,
      message: msgText,
    };
    if (attachmentUrl) {
      insertPayload.attachment_url = attachmentUrl;
      insertPayload.file_url = attachmentUrl;
      insertPayload.file_name = attachFileName;
      insertPayload.file_type = attachMimeType;
    }

    const { error: insertErr } = await supabase
      .from("consultation_messages")
      .insert(insertPayload);

    if (insertErr) {
      setSendError(`Gửi thất bại: ${insertErr.message}`);
      setSendingReply(false);
      return;
    }

    // Update inquiry status + file_url
    const updatePayload: Record<string, unknown> = { status: "new" };
    if (attachmentUrl) {
      updatePayload.file_url = attachmentUrl;
      updatePayload.file_type = attachMimeType ?? (isPdfAttach ? "application/pdf" : "file");
    }
    await supabase.from("inquiries").update(updatePayload).eq("id", selected.id);

    setCustomerReply("");
    setAttachFile(null);
    setSendingReply(false);
  };

  // ── Recall ──
  const handleRecallMsg = useCallback(async (msg: ChatMsg) => {
    if (!msg.id) return;
    await supabase.from("consultation_messages").update({ is_recalled: true }).eq("id", msg.id);
    setSelected((prev) =>
      prev ? { ...prev, messages: (prev.messages ?? []).map((m) => m.id === msg.id ? { ...m, is_recalled: true } : m) } : prev
    );
  }, []);

  // ── Edit ──
  const startEdit = useCallback((msg: ChatMsg) => {
    setEditingMsg(msg);
    setEditText(msg.message ?? "");
    setTimeout(() => editInputRef.current?.focus(), 100);
  }, []);

  const saveEdit = async () => {
    if (!editingMsg?.id || !editText.trim()) return;
    setSavingEdit(true);
    const now = new Date().toISOString();
    await supabase.from("consultation_messages").update({ message: editText.trim(), is_edited: true, edited_at: now }).eq("id", editingMsg.id);
    setSelected((prev) => {
      if (!prev) return prev;
      return { ...prev, messages: (prev.messages ?? []).map((m) => m.id === editingMsg.id ? { ...m, message: editText.trim(), is_edited: true, edited_at: now } : m) };
    });
    setEditingMsg(null);
    setEditText("");
    setSavingEdit(false);
  };

  const handleCopyMsg = useCallback((msg: ChatMsg) => {
    if (msg.message) {
      navigator.clipboard.writeText(msg.message).catch(() => {});
      setCopiedId(msg.id ?? null);
      setTimeout(() => setCopiedId(null), 2000);
    }
  }, []);

  const handleShareMsg = useCallback((msg: ChatMsg) => {
    const text = msg.message || msg.attachment_url || "";
    if (navigator.share) { navigator.share({ text, title: "Tin nhắn từ Bizwell" }).catch(() => {}); }
    else { navigator.clipboard.writeText(text).catch(() => {}); }
  }, []);

  const handleContextMenu = (e: React.MouseEvent, msg: ChatMsg) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, msg });
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />

      {/* PDF Viewer Modal */}
      {pdfViewer && <PdfViewerModal url={pdfViewer.url} fileName={pdfViewer.fileName} onClose={() => setPdfViewer(null)} />}

      {/* Context Menu */}
      {contextMenu && (
        <MessageContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          msg={contextMenu.msg}
          isOwn={contextMenu.msg.sender_type === "customer"}
          onClose={() => setContextMenu(null)}
          onCopy={() => handleCopyMsg(contextMenu.msg)}
          onRecall={() => handleRecallMsg(contextMenu.msg)}
          onEdit={() => startEdit(contextMenu.msg)}
          onShare={() => handleShareMsg(contextMenu.msg)}
        />
      )}

      {/* Edit Modal */}
      {editingMsg && (
        <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-edit-line text-amber-500" /> Chỉnh sửa tin nhắn
              </h3>
              <button onClick={() => setEditingMsg(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>
            <textarea ref={editInputRef} value={editText} onChange={(e) => setEditText(e.target.value)} rows={4} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#004A99] resize-none" />
            <div className="flex gap-2 mt-4 justify-end">
              <button onClick={() => setEditingMsg(null)} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap">Hủy</button>
              <button onClick={saveEdit} disabled={savingEdit || !editText.trim()} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-amber-600 disabled:opacity-50 whitespace-nowrap">
                {savingEdit ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
                Lưu thay đổi
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-8">
          <Link to="/" className="hover:text-[#004A99] cursor-pointer transition-colors">Trang chủ</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-gray-600 font-medium">Tra cứu yêu cầu tư vấn</span>
          {step === "detail" && selected && (
            <>
              <i className="ri-arrow-right-s-line" />
              <span className="text-gray-600 font-medium truncate max-w-[200px]">{selected.product_name || "Chi tiết"}</span>
            </>
          )}
        </div>

        {/* ── SEARCH STEP ── */}
        {step === "search" && (
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-10">
              <div className="w-16 h-16 bg-[#004A99]/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <i className="ri-search-eye-line text-[#004A99] text-3xl" />
              </div>
              <h1 className="text-3xl font-black text-[#212529] mb-3">Tra cứu yêu cầu tư vấn</h1>
              <p className="text-gray-500 text-sm leading-relaxed">
                Nhập email hoặc số điện thoại bạn đã dùng khi gửi yêu cầu tư vấn để xem trạng thái và phản hồi từ chuyên gia.
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-8">
              <form onSubmit={handleSearch} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-mail-line mr-1.5 text-gray-400" /> Email đăng ký
                  </label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#004A99] focus:ring-1 focus:ring-[#004A99]/20 transition-all" />
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs text-gray-400 font-medium">hoặc</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="ri-phone-line mr-1.5 text-gray-400" /> Số điện thoại
                  </label>
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0901234567" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#004A99] focus:ring-1 focus:ring-[#004A99]/20 transition-all" />
                </div>
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-start gap-2">
                    <i className="ri-error-warning-line flex-shrink-0 mt-0.5" /> {error}
                  </div>
                )}
                <button type="submit" disabled={searchLoading} className="w-full bg-[#004A99] hover:bg-[#003a7a] text-white font-bold py-3.5 rounded-xl text-sm cursor-pointer transition-colors disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap">
                  {searchLoading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-search-line" />}
                  {searchLoading ? "Đang tra cứu..." : "Tra cứu ngay"}
                </button>
              </form>
            </div>

            <div className="mt-6 bg-[#EDF2FB] rounded-xl p-4 text-xs text-[#004A99]/80">
              <p className="font-semibold mb-1 flex items-center gap-1.5"><i className="ri-information-line" /> Lưu ý</p>
              <ul className="space-y-1 list-disc pl-4">
                <li>Chỉ hiển thị yêu cầu tư vấn bạn đã gửi qua website</li>
                <li>Phản hồi của chuyên gia sẽ xuất hiện tại đây sau khi xử lý</li>
                <li>Nếu không nhận được phản hồi trong 24h, vui lòng liên hệ Zalo để được hỗ trợ nhanh hơn</li>
              </ul>
            </div>
          </div>
        )}

        {/* ── RESULTS STEP ── */}
        {step === "results" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-black text-[#212529]">Yêu cầu của bạn</h2>
                <p className="text-sm text-gray-500 mt-1">Tìm thấy <strong>{inquiries.length}</strong> yêu cầu tư vấn</p>
              </div>
              <button onClick={() => { setStep("search"); setInquiries([]); setError(""); }} className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors">
                <i className="ri-arrow-left-line" /> Tra cứu lại
              </button>
            </div>

            <div className="space-y-3">
              {inquiries.map((inq) => {
                const status = STATUS_CONFIG[inq.status ?? "new"] ?? STATUS_CONFIG["new"];
                return (
                  <button key={inq.id} onClick={() => openDetail(inq)} className="w-full text-left bg-white rounded-2xl border border-gray-200 p-5 hover:border-[#004A99]/30 transition-all cursor-pointer group">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`text-xs px-2.5 py-1 rounded-full font-semibold flex items-center gap-1.5 ${status.color}`}>
                            <i className={status.icon} /> {status.label}
                          </span>
                          {inq.status === "replied" && (
                            <span className="text-xs text-green-600 font-semibold flex items-center gap-1">
                              <i className="ri-star-line" /> Có phản hồi mới
                            </span>
                          )}
                        </div>
                        <h3 className="font-bold text-gray-900 text-sm group-hover:text-[#004A99] transition-colors">{inq.product_name || "Yêu cầu tư vấn chung"}</h3>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2 leading-relaxed">{inq.message}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-gray-400">{inq.created_at?.slice(0, 10)}</p>
                        <div className="mt-2 flex items-center gap-1 text-xs text-[#004A99] font-semibold group-hover:gap-2 transition-all">
                          Xem chi tiết <i className="ri-arrow-right-line" />
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 bg-white rounded-2xl border border-gray-200 p-6 text-center">
              <p className="text-sm text-gray-600 mb-4">Cần hỗ trợ nhanh hơn hoặc có thắc mắc khác?</p>
              <div className="flex justify-center items-center">
                <ZaloLink className="inline-flex items-center justify-center gap-2 bg-[#0068FF] hover:bg-[#0054CC] text-white px-5 py-2.5 rounded-full font-semibold text-sm cursor-pointer transition-all hover:scale-105 whitespace-nowrap shadow-sm hover:shadow-md w-[220px]" tooltipPosition="top" title="Chat trực tiếp qua Zalo">
                  <i className="ri-message-3-line text-base" /> Chat Zalo ngay
                </ZaloLink>
              </div>
            </div>
          </div>
        )}

        {/* ── DETAIL STEP ── */}
        {step === "detail" && selected && (
          <div>
            <button onClick={() => setStep("results")} className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#004A99] mb-6 cursor-pointer transition-colors group">
              <i className="ri-arrow-left-line group-hover:-translate-x-0.5 transition-transform" /> Quay lại danh sách
            </button>

            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-[#001428] to-[#004A99] px-6 py-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-white font-bold text-lg leading-tight">{selected.product_name || "Yêu cầu tư vấn chung"}</h2>
                    <p className="text-white/60 text-xs mt-1">Gửi ngày {selected.created_at?.slice(0, 10)} · {selected.name}</p>
                  </div>
                  <div>
                    {(() => {
                      const st = STATUS_CONFIG[selected.status ?? "new"] ?? STATUS_CONFIG["new"];
                      return (
                        <span className="text-xs px-3 py-1.5 rounded-full font-semibold flex items-center gap-1.5 bg-white/10 text-white">
                          <i className={st.icon} /> {st.label}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Real-time indicator + search toggle */}
              <div className="px-6 py-2 bg-green-50 border-b border-green-100 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse flex-shrink-0" />
                  <p className="text-xs text-green-700 font-medium">Kết nối real-time — Tin nhắn mới sẽ hiển thị ngay lập tức</p>
                </div>
                <button
                  onClick={() => { setChatSearchOpen(!chatSearchOpen); if (chatSearchOpen) setChatSearch(""); }}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap ${chatSearchOpen ? "bg-yellow-400 text-gray-900" : "bg-green-100 text-green-700 hover:bg-green-200"}`}
                >
                  <i className="ri-search-line" /> Tìm trong chat
                </button>
              </div>

              {/* Chat search bar */}
              {chatSearchOpen && (
                <div className="px-6 py-2.5 bg-yellow-50 border-b border-yellow-200 flex items-center gap-2">
                  <i className="ri-search-line text-yellow-600 text-sm flex-shrink-0" />
                  <input type="text" value={chatSearch} onChange={(e) => setChatSearch(e.target.value)} placeholder="Tìm kiếm nội dung trong cuộc trò chuyện..." autoFocus className="flex-1 bg-transparent text-sm outline-none text-gray-800 placeholder-yellow-400" />
                  {chatSearch && (
                    <span className="text-xs text-yellow-700 font-semibold whitespace-nowrap">
                      {chatSearchResults.length > 0 ? `${chatSearchIdx + 1}/${chatSearchResults.length} kết quả` : "Không tìm thấy"}
                    </span>
                  )}
                  {chatSearchResults.length > 1 && (
                    <>
                      <button onClick={() => navigateSearch("prev")} className="w-6 h-6 flex items-center justify-center rounded hover:bg-yellow-200 cursor-pointer"><i className="ri-arrow-up-s-line text-yellow-700 text-sm" /></button>
                      <button onClick={() => navigateSearch("next")} className="w-6 h-6 flex items-center justify-center rounded hover:bg-yellow-200 cursor-pointer"><i className="ri-arrow-down-s-line text-yellow-700 text-sm" /></button>
                    </>
                  )}
                  <button onClick={() => { setChatSearch(""); setChatSearchOpen(false); }} className="w-6 h-6 flex items-center justify-center rounded hover:bg-yellow-200 cursor-pointer"><i className="ri-close-line text-yellow-700 text-sm" /></button>
                </div>
              )}

              {/* Chat timeline — scroll inside this container */}
              <div
                ref={chatContainerRef}
                className="p-6 space-y-5 min-h-[300px] max-h-[500px] overflow-y-auto bg-[#FAFBFC]"
              >
                {selected.messages && selected.messages.length > 0 ? (
                  selected.messages.map((msg, i) => (
                    <div key={msg.id ?? i} ref={(el) => { if (msg.id) msgRefs.current[msg.id] = el; }}>
                      <ChatBubble
                        msg={msg}
                        isOwn={msg.sender_type === "customer"}
                        isCopied={copiedId === msg.id}
                        onContextMenu={handleContextMenu}
                        onCopy={handleCopyMsg}
                        onOpenContextMenu={handleContextMenu}
                        onPdfPreview={(url, fileName) => setPdfViewer({ url, fileName })}
                        ownerLabel="Bạn"
                        otherLabel="Chuyên gia Bizwell"
                        searchQuery={chatSearch}
                      />
                    </div>
                  ))
                ) : (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                      <i className="ri-chat-3-line text-gray-300 text-2xl" />
                    </div>
                    <p className="text-sm text-gray-500 font-medium">Chuyên gia đang xử lý yêu cầu của bạn</p>
                    <p className="text-xs text-gray-400 mt-1">Thời gian phản hồi thường trong vòng 1-24 giờ làm việc</p>
                  </div>
                )}
              </div>

              {/* Customer reply input */}
              <div className="border-t border-gray-200 px-6 py-4 bg-white">
                <p className="text-xs font-semibold text-gray-600 mb-2">Gửi thêm câu hỏi hoặc thông tin bổ sung:</p>
                {attachFile && (
                  <div className="flex items-center gap-2 mb-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
                    <i className={`text-xs ${attachFile.type === "application/pdf" ? "ri-file-pdf-2-line text-red-500" : attachFile.type.startsWith("audio/") ? "ri-music-line text-purple-500" : "ri-attachment-2 text-blue-500"}`} />
                    <span className="text-xs text-blue-700 truncate flex-1">{attachFile.name}</span>
                    <span className="text-[10px] text-blue-400">{(attachFile.size / 1024).toFixed(0)} KB</span>
                    <button onClick={() => setAttachFile(null)} className="cursor-pointer text-blue-400 hover:text-red-500 ml-1"><i className="ri-close-line text-xs" /></button>
                  </div>
                )}
                <div className="flex gap-3">
                  <input ref={fileInputRef} type="file" className="hidden" accept="image/*,.pdf,.doc,.docx,.mp3,.wav,.ogg,.m4a" onChange={(e) => e.target.files?.[0] && setAttachFile(e.target.files[0])} />
                  <button onClick={() => fileInputRef.current?.click()} className="self-end w-9 h-9 flex items-center justify-center border border-gray-200 rounded-xl hover:bg-gray-100 cursor-pointer flex-shrink-0 transition-colors" title="Đính kèm file (PDF, ảnh, audio)">
                    <i className="ri-attachment-2 text-gray-400 text-sm" />
                  </button>
                  <textarea
                    value={customerReply}
                    onChange={(e) => setCustomerReply(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendCustomerMessage(); } }}
                    rows={2}
                    placeholder="Nhập câu hỏi thêm... (Enter để gửi, Shift+Enter xuống dòng)"
                    className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#004A99] resize-none bg-white"
                  />
                  <button
                    onClick={sendCustomerMessage}
                    disabled={sendingReply || attachUploading || (!customerReply.trim() && !attachFile)}
                    className="self-end flex items-center gap-2 px-4 py-2.5 bg-[#004A99] text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-[#003a7a] disabled:opacity-50 whitespace-nowrap transition-colors"
                  >
                    {(sendingReply || attachUploading) ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-send-plane-line" />}
                    {attachUploading ? "Đang tải..." : "Gửi"}
                  </button>
                </div>
                {sendError && (
                  <div className="mt-2 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">
                    <i className="ri-error-warning-line flex-shrink-0" />
                    {sendError}
                    <button onClick={() => setSendError(null)} className="ml-auto cursor-pointer text-red-400 hover:text-red-600">
                      <i className="ri-close-line" />
                    </button>
                  </div>
                )}
                <p className="text-[10px] text-gray-400 mt-1.5">
                  <i className="ri-information-line mr-1" />
                  Hỗ trợ: ảnh, PDF, audio (MP3/WAV). Nhấp chuột phải vào tin nhắn để chỉnh sửa hoặc thu hồi. Nhấn vào ảnh để phóng to.
                </p>
              </div>

              {/* Zalo CTA */}
              <div className="border-t border-gray-100 px-6 py-5">
                <p className="text-sm text-gray-700 font-semibold mb-1">Bạn vẫn còn thắc mắc?</p>
                <p className="text-xs text-gray-500 mb-4">Chat trực tiếp qua Zalo ngay để được chuyên gia tư vấn nhanh chóng và chi tiết hơn.</p>
                <div className="flex justify-center items-center">
                  <ZaloLink className="inline-flex items-center justify-center gap-2 bg-[#0068FF] hover:bg-[#0054CC] text-white px-5 py-2.5 rounded-full font-semibold text-sm cursor-pointer transition-all hover:scale-105 whitespace-nowrap shadow-sm hover:shadow-md w-[220px]" tooltipPosition="top" title="Nhắn tin qua Zalo">
                    <i className="ri-message-3-line" /> Nhắn tin Zalo
                  </ZaloLink>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Zalo floating button */}
      <ZaloLink
        className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 flex items-center justify-center gap-2 bg-[#0068FF] hover:bg-[#0054CC] text-white px-4 py-2.5 rounded-full cursor-pointer transition-all hover:scale-105 hover:shadow-lg whitespace-nowrap w-[200px] shadow-md"
        tooltipPosition="top"
        title="Liên hệ Zalo"
      >
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          <i className="ri-message-3-line text-base" />
        </div>
        <div className="text-left">
          <p className="text-xs font-bold leading-tight">Liên hệ Zalo</p>
          <p className="text-[10px] text-white/80 leading-tight">Chat trực tiếp ngay</p>
        </div>
      </ZaloLink>

      <Footer />
    </div>
  );
};

export default MyRequestsPage;
